import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CURRENT_TOUR_VERSION } from '@shared/services/tutorial.service';

interface OnboardingTourStore {
  /** Version of the tour the user last completed, or null if never seen. */
  lastSeenVersion: number | null;
  /** Number of steps in the current tour run (effective steps for this user). */
  effectiveStepCount: number;
  showWelcome: boolean;
  isTourActive: boolean;
  showCompletion: boolean;
  currentStep: number;
  isFloorMapReady: boolean;
  openWelcome: () => void;
  dismissWelcome: () => void;
  startTour: () => void;
  endTour: () => void;
  setCurrentStep: (step: number) => void;
  setEffectiveStepCount: (count: number) => void;
  /** Called when the user finishes the tour — marks the current version as seen. */
  completeTour: () => void;
  closeCompletion: () => void;
  resetTour: () => void;
  setFloorMapReady: () => void;
}

export const useOnboardingTourStore = create<OnboardingTourStore>()(
  persist(
    (set) => ({
      lastSeenVersion: null,
      effectiveStepCount: 0,
      showWelcome: false,
      isTourActive: false,
      showCompletion: false,
      currentStep: 0,
      isFloorMapReady: false,

      openWelcome: () => set({ showWelcome: true }),
      setFloorMapReady: () => set({ isFloorMapReady: true }),

      // "Maybe later" — just hide the modal; do NOT mark as seen so the tour
      // is shown again on the next session.
      dismissWelcome: () => set({ showWelcome: false }),

      startTour: () =>
        set({ showWelcome: false, isTourActive: true, currentStep: 0, showCompletion: false }),

      endTour: () => set({ isTourActive: false }),

      setCurrentStep: (step) => set({ currentStep: step }),
      setEffectiveStepCount: (count) => set({ effectiveStepCount: count }),

      // Finish button — mark the current version as seen permanently.
      completeTour: () =>
        set({ isTourActive: false, showCompletion: true, lastSeenVersion: CURRENT_TOUR_VERSION }),

      closeCompletion: () => set({ showCompletion: false }),

      resetTour: () =>
        set({
          lastSeenVersion: null,
          showWelcome: true,
          isTourActive: false,
          showCompletion: false,
          currentStep: 0,
        }),
    }),
    {
      name: 'nexgen-onboarding-tour',
      partialize: (state) => ({ lastSeenVersion: state.lastSeenVersion }),
    }
  )
);
